import clsx from 'clsx';
import React, { forwardRef, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import Checkmark from '~/icons/checkmark.svg';
import KlarnaIcon from '~/icons/payment-klarna.svg';
import AltaPayPayment, { AltaPayPaymentType } from '~/services/altapay/payment';
import { AltaPay, ExtendedAltaPayPaymentMethod } from '~/services/altapay/types';
import { Text } from '~/shared/components';
import { NetsPaymentType, PaymentMethod } from '~/shared/constants';
import { PaymentOptionLoader } from '../PaymentOptions/OptionsLoader';
import styles from './PaymentOption.module.scss';

const PaymentLogo: React.FC<{ name: string; src: string; height?: number }> = ({
    name,
    src,
    height,
}) => {
    // We'll use an <img> element in favour of next/image because this is not responsive nor does it have a defined width/aspect ratio.
    // eslint-disable-next-line @next/next/no-img-element
    return <img className={styles.logo} style={{ height }} src={src} title={name} alt={name} />;
};

export const CreditCardPaymentLogos: React.FC = () => (
    <div className={styles.creditCardLogos}>
        <PaymentLogo src="/assets/payment/logo-visa.png" name="Visa" />
        <PaymentLogo src="/assets/payment/logo-dankort.png" name="Dankort" />
        <PaymentLogo src="/assets/payment/logo-maestro.png" name="Maestro" />
        <PaymentLogo src="/assets/payment/logo-mastercard.png" name="Mastercard" />
    </div>
);

export interface PaymentOptionProps {
    id: string;
    label: string;
    value: string;
    disabled?: boolean;
    isSelected?: boolean;
    isLoading?: boolean;
    logo?: React.ReactNode | string;
    sortOrder?: number;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    children?: React.ReactNode | ((ref: RefObject<HTMLDivElement>) => React.ReactNode);
}

const PaymentOption = forwardRef<HTMLDivElement, PaymentOptionProps>(
    ({ id, children, disabled, isSelected, isLoading, label, value, logo, onChange }, ref) => {
        const media = useMemo(() => {
            if (typeof logo === 'string') {
                return <PaymentLogo name={label} src={logo} />;
            }

            return logo;
        }, [logo]);

        if (isLoading) return <PaymentOptionLoader />;

        return (
            <div ref={ref} className={styles.paymentOption}>
                <div className={styles.wrapper}>
                    <input
                        className={styles.input}
                        id={id}
                        disabled={disabled}
                        type="radio"
                        name="payment-method"
                        value={value}
                        onChange={onChange}
                    />

                    <label className={styles.label} htmlFor={id}>
                        <span className={styles.indicator}>
                            <Checkmark className={styles.checkmark} />
                        </span>

                        <Text className={styles.name} as="span" variant="bodySmall">
                            {label}
                        </Text>

                        {media}
                    </label>
                </div>

                <div
                    className={clsx(styles.content, {
                        [styles.isVisible]: isSelected && !!children,
                    })}
                >
                    {children}
                </div>
            </div>
        );
    },
);

PaymentOption.displayName = 'PaymentOption';

export type AltaPayPaymentOptionProps = Pick<
    PaymentOptionProps,
    'children' | 'isSelected' | 'isLoading' | 'disabled'
> & {
    method: ExtendedAltaPayPaymentMethod;
    onChange: (value: AltaPay.Schemas['PaymentMethod'], ref?: RefObject<HTMLDivElement>) => void;
};

export const AltaPayPaymentOption = forwardRef<HTMLDivElement, AltaPayPaymentOptionProps>(
    ({ children, disabled, isSelected, isLoading, method, onChange }, ref) => {
        const { id, label, logoUrl, onRenderCheck } = method;

        // If onRenderCheck is defined, the payment option needs to load first.
        const [isRenderChecking, setIsRenderChecking] = useState<boolean>(
            onRenderCheck !== undefined,
        );
        const [canRender, setCanRender] = useState<boolean>(true);

        const inputId = `payment-${id}`;

        const payment = useMemo(() => new AltaPayPayment(method), [id]);

        const onOptionChange = useCallback(
            (_: React.ChangeEvent<HTMLInputElement>) => {
                onChange(method);
            },
            [onChange],
        );

        useEffect(() => {
            payment
                .canRender()
                .then((shouldRender) => setCanRender(shouldRender))
                .finally(() => setIsRenderChecking(false));
        }, []);

        const logo = useMemo(() => {
            switch (payment.type) {
                case AltaPayPaymentType.APPLEPAY:
                    return <PaymentLogo src="/assets/payment/logo-applepay.png" name={label} />;

                case AltaPayPaymentType.MOBILEPAY:
                    return <PaymentLogo src="/assets/payment/logo-mobilepay.png" name={label} />;

                case AltaPayPaymentType.KLARNA:
                    return <KlarnaIcon className={styles.klarnaLogo} title={label} />;

                case AltaPayPaymentType.CREDIT_CARD:
                    return <CreditCardPaymentLogos />;

                default:
                    return <PaymentLogo src={logoUrl} name={label} />;
            }
        }, [id, payment.type]);

        if (!canRender) return null;

        return (
            <PaymentOption
                ref={ref}
                logo={logo}
                id={inputId}
                disabled={disabled}
                value={id}
                label={label}
                isSelected={isSelected}
                isLoading={isRenderChecking || isLoading}
                onChange={onOptionChange}
            >
                {children}
            </PaymentOption>
        );
    },
);

AltaPayPaymentOption.displayName = 'AltaPayPaymentOption';

type NetsPaymentOptionProps = Omit<PaymentOptionProps, 'logo' | 'value'> & {};

export const NetsPaymentOption: React.FC<NetsPaymentOptionProps> = ({ id, ...rest }) => {
    const logo =
        id === NetsPaymentType.MOBILEPAY ? (
            <PaymentLogo src="/assets/payment/logo-mobilepay.png" name="MobilePay" />
        ) : (
            <CreditCardPaymentLogos />
        );

    return <PaymentOption {...rest} id={id} value={PaymentMethod.NETS} logo={logo} />;
};

export default PaymentOption;
